import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import arrowRight from '~/assets/icons/arrow_right.svg';
import { Divider } from '~/components/ui/Divider';
import { ImgIcon } from '~/components/ui/ImgIcon';
import { SectionHeading } from '~/components/ui/SectionHeading';
import { Select } from '~/components/ui/Select';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import { hexToRgb, isRgbPaintType, VehiclePaintType } from '~/shared';
import { usePaintColors, usePaintPreview } from '../../hooks';
import {
	setBrakesColor,
	setGroup,
	setIndexedColor,
	setPearlColor,
	setRimsColor,
	setSelectedColor,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { ColorSelectColorItem } from '../ColorSelectColorItem';
import { RGBPalette } from '../RGBPalette';
import { CategoryTypes, colorSelectItems } from './ColorSelect.model';
import {
	StyledContainer,
	StyledGridButton,
	StyledPainingWheelsInfoContainer,
	StyledPaintingWheelsColorsGrid,
	StyledPaintingWheelsColorsGridFadeRight,
	StyledPaintingWheelsColorsGridMaskViewport,
	StyledPaintingWheelsColorsGridTrack,
	StyledPaintingWheelsColorsGridWrapper,
	StyledPaintingWheelsContainer,
	StyledSelectContainer,
	StyledSettingsContainer,
} from './ColorSelect.styled';

type RimsColorForUi = {
	readonly index: number;
	readonly name: string;
	readonly hex: string | null;
};

function rimsColorPreviewCss(hex: string | null | undefined): string {
	if (!hex?.trim()) {
		return 'rgba(255, 255, 255, 0.15)';
	}
	const t = hex.trim();
	return t.startsWith('#') ? t : `#${t}`;
}

const ColorSelect = () => {
	const dispatch = useLayoutDispatch();
	const activeIndex = useLayoutSelector(
		(state) => state.tuningStudio.activeIndex,
	);
	const paintGroups = useObservable(
		rage.vehiclePaintShop.getVehiclePaintTypes(),
	);
	const vehicleIndexedColors = useObservable(
		rage.vehiclePaintShop.getVehicleIndexedColors(),
	);
	const pearlColors = useObservable(
		rage.vehiclePaintShop.getVehiclePearlColors(),
	);
	const rimsColors = useObservable(
		rage.vehiclePaintShop.getVehicleRimsColors(),
	);
	const brakesColorList = useObservable(
		rage.vehiclePaintShop.getVehicleBrakesColors(),
	);
	const activePaintGroup = useLayoutSelector(
		(state) => state.colorPicker.paintGroup,
	);
	const indexedColor = useLayoutSelector(
		(state) => state.colorPicker.indexedColor,
	);
	const pearlColor = useLayoutSelector((state) => state.colorPicker.pearlColor);
	const rimsColor = useLayoutSelector((state) => state.colorPicker.rimsColor);
	const brakesColor = useLayoutSelector(
		(state) => state.colorPicker.brakesColor,
	);
	const ownedVehiclePaint = useLayoutSelector(
		(state) => state.carPaint.ownedVehiclePaint,
	);
	const { colorList, activeColor } = usePaintColors();

	const currentItem = colorSelectItems[activeIndex];
	const isPaintingWheels = currentItem?.type === CategoryTypes.PaintingWheels;
	const isPaintingInterior =
		currentItem?.type === CategoryTypes.PaintingInterior;
	const isPaintingWorks = currentItem?.type === CategoryTypes.PaintingWorks;
	const isBrakes = currentItem?.type === CategoryTypes.Brakes;
	usePaintPreview();

	const paintingWheelsGridContainerRef = useRef<HTMLDivElement>(null);
	const paintingWheelsGridTrackRef = useRef<HTMLDivElement>(null);
	const [paintingWheelsGridOffset, setPaintingWheelsGridOffset] = useState(0);
	const [paintingWheelsGridMaxOffset, setPaintingWheelsGridMaxOffset] =
		useState(0);

	const brakesGridContainerRef = useRef<HTMLDivElement>(null);
	const brakesGridTrackRef = useRef<HTMLDivElement>(null);
	const [brakesGridOffset, setBrakesGridOffset] = useState(0);
	const [brakesGridMaxOffset, setBrakesGridMaxOffset] = useState(0);

	const updatePaintingWheelsGridMetrics = useCallback(() => {
		const container = paintingWheelsGridContainerRef.current;
		const track = paintingWheelsGridTrackRef.current;
		if (!container || !track) {
			return;
		}
		const max = Math.max(0, track.scrollWidth - container.clientWidth);
		setPaintingWheelsGridMaxOffset(max);
		setPaintingWheelsGridOffset((prev) => Math.min(Math.max(0, prev), max));
	}, []);

	const updateBrakesGridMetrics = useCallback(() => {
		const container = brakesGridContainerRef.current;
		const track = brakesGridTrackRef.current;
		if (!container || !track) {
			return;
		}
		const max = Math.max(0, track.scrollWidth - container.clientWidth);
		setBrakesGridMaxOffset(max);
		setBrakesGridOffset((prev) => Math.min(Math.max(0, prev), max));
	}, []);

	useLayoutEffect(() => {
		if (!isPaintingWheels) {
			return;
		}
		updatePaintingWheelsGridMetrics();
	}, [isPaintingWheels, updatePaintingWheelsGridMetrics, rimsColors.length]);

	useLayoutEffect(() => {
		if (!isBrakes) {
			return;
		}
		updateBrakesGridMetrics();
	}, [isBrakes, updateBrakesGridMetrics, brakesColorList.length]);

	useEffect(() => {
		if (isPaintingWheels) {
			setPaintingWheelsGridOffset(0);
		}
	}, [activeIndex, isPaintingWheels]);

	useEffect(() => {
		if (isBrakes) {
			setBrakesGridOffset(0);
		}
	}, [activeIndex, isBrakes]);

	const epsilon = 2;
	const paintingWheelsGridAtStart = paintingWheelsGridOffset <= epsilon;
	const paintingWheelsGridAtEnd =
		paintingWheelsGridMaxOffset <= epsilon ||
		paintingWheelsGridOffset >= paintingWheelsGridMaxOffset - epsilon;
	const paintingWheelsGridHasOverflow = paintingWheelsGridMaxOffset > epsilon;
	const paintingWheelsGridShowLeftFade =
		paintingWheelsGridHasOverflow && !paintingWheelsGridAtStart;
	const paintingWheelsGridShowRightFade =
		paintingWheelsGridHasOverflow && !paintingWheelsGridAtEnd;

	const brakesGridAtStart = brakesGridOffset <= epsilon;
	const brakesGridAtEnd =
		brakesGridMaxOffset <= epsilon ||
		brakesGridOffset >= brakesGridMaxOffset - epsilon;
	const brakesGridHasOverflow = brakesGridMaxOffset > epsilon;
	const brakesGridShowLeftFade = brakesGridHasOverflow && !brakesGridAtStart;
	const brakesGridShowRightFade = brakesGridHasOverflow && !brakesGridAtEnd;

	const goPaintingWheelsGridToStart = useCallback(() => {
		setPaintingWheelsGridOffset(0);
	}, []);

	const goPaintingWheelsGridToEnd = useCallback(() => {
		const container = paintingWheelsGridContainerRef.current;
		const track = paintingWheelsGridTrackRef.current;
		if (!container || !track) {
			return;
		}
		const max = Math.max(0, track.scrollWidth - container.clientWidth);
		setPaintingWheelsGridOffset(max);
	}, []);

	const goBrakesGridToStart = useCallback(() => {
		setBrakesGridOffset(0);
	}, []);

	const goBrakesGridToEnd = useCallback(() => {
		const container = brakesGridContainerRef.current;
		const track = brakesGridTrackRef.current;
		if (!container || !track) {
			return;
		}
		const max = Math.max(0, track.scrollWidth - container.clientWidth);
		setBrakesGridOffset(max);
	}, []);

	const ownedVehiclePaintRef = useRef(ownedVehiclePaint);
	const paintGroupsRef = useRef(paintGroups);
	ownedVehiclePaintRef.current = ownedVehiclePaint;
	paintGroupsRef.current = paintGroups;

	useEffect(() => {
		const owned = ownedVehiclePaintRef.current;
		if (!owned) return;

		const groups = paintGroupsRef.current;
		const {
			bodyColor,
			interiorColor,
			rimsColor: ownedRimsColor,
			brakesColor,
		} = owned;

		if (isPaintingWorks && bodyColor) {
			const group = groups.find((g) => g.type === bodyColor.type);
			dispatch(setGroup(group ?? null));

			if (isRgbPaintType(bodyColor.type)) {
				dispatch(setSelectedColor(hexToRgb(bodyColor.color as string)));
			} else {
				dispatch(setIndexedColor(bodyColor.color as number));
				dispatch(setSelectedColor({ r: 255, g: 255, b: 255 }));
			}

			if (
				bodyColor.type === VehiclePaintType.Pearl &&
				typeof bodyColor.pearlColor === 'number'
			) {
				dispatch(setPearlColor(bodyColor.pearlColor));
			}

			const pearlIdx =
				bodyColor.type === VehiclePaintType.Pearl
					? bodyColor.pearlColor ?? null
					: null;
			rage.vehiclePaintShop.previewBodyColor(
				bodyColor.color,
				bodyColor.type,
				pearlIdx,
			);
		}

		if (isPaintingInterior && interiorColor) {
			const rgbGroup = groups.find((g) => isRgbPaintType(g.type));
			if (rgbGroup) dispatch(setGroup(rgbGroup));
			dispatch(setSelectedColor(hexToRgb(interiorColor as string)));
			rage.vehiclePaintShop.previewInteriorColor(interiorColor as string);
		}

		if (isBrakes) {
			const activeBrakesColor = brakesColor ?? 0;
			dispatch(setBrakesColor(activeBrakesColor));
			rage.vehiclePaintShop.previewBrakesColor(activeBrakesColor);
		}

		if (isPaintingWheels && ownedRimsColor) {
			dispatch(setRimsColor(ownedRimsColor));
			rage.vehiclePaintShop.previewRimsColor(ownedRimsColor);
		}
	}, [activeIndex]);

	useEffect(() => {
		return () => {
			const owned = ownedVehiclePaintRef.current;
			if (!owned?.bodyColor) return;

			const {
				bodyColor,
				interiorColor,
				rimsColor: ownedRims,
				brakesColor,
			} = owned;
			const pearlIdx =
				bodyColor.type === VehiclePaintType.Pearl
					? bodyColor.pearlColor ?? null
					: null;

			rage.vehiclePaintShop.previewBodyColor(
				bodyColor.color,
				bodyColor.type,
				pearlIdx,
			);
			if (interiorColor) {
				rage.vehiclePaintShop.previewInteriorColor(interiorColor as string);
			}
			if (ownedRims) {
				rage.vehiclePaintShop.previewRimsColor(ownedRims);
			}
			if (brakesColor != null) {
				rage.vehiclePaintShop.previewBrakesColor(brakesColor);
			}
		};
	}, []);

	const handleSelectPaintGroup = useCallback(
		(value: string | null) => {
			if (!value) {
				return;
			}

			const selectedGroup = paintGroups.find((item) => item.name === value);
			if (!selectedGroup) return;

			dispatch(setGroup(selectedGroup));

			const isSpecialPaintType = [
				VehiclePaintType.Chameleon,
				VehiclePaintType.Pearl,
			].includes(selectedGroup.type);

			if (!isSpecialPaintType) {
				dispatch(setIndexedColor(0));
				return;
			}

			if (
				!isRgbPaintType(selectedGroup.type) &&
				selectedGroup.type !== VehiclePaintType.Chrome
			) {
				const colors = vehicleIndexedColors.filter(
					(item) => item.type === selectedGroup.type,
				);
				if (colors.length > 0) {
					dispatch(setIndexedColor(colors[0].index));
				}
			}

			const isPearl = selectedGroup.type === VehiclePaintType.Pearl;

			if (isPearl && isPaintingWheels && !rimsColor) {
				dispatch(setRimsColor(rimsColors[0]?.index || 0));
				return;
			}

			if (isPearl && !pearlColor) {
				dispatch(setPearlColor(pearlColors[0]?.index || 0));
				return;
			}
		},
		[
			vehicleIndexedColors,
			paintGroups,
			pearlColors,
			pearlColor,
			rimsColor,
			indexedColor,
			isPaintingWheels,
			rimsColors,
		],
	);

	const handlePaintingWheelsGridPick = useCallback(
		(index: number) => {
			dispatch(setRimsColor(index));
		},
		[dispatch],
	);

	const handleBrakesGridPick = useCallback(
		(index: number) => {
			dispatch(setBrakesColor(index));
		},
		[dispatch],
	);

	const handleSelectColor = useCallback(
		(value: string | null) => {
			if (!value) {
				return;
			}

			if (isBrakes) {
				const selectedColor = brakesColorList.find(
					(item) => item.name === value,
				);
				if (selectedColor) {
					dispatch(setBrakesColor(selectedColor.index));
				}
				return;
			}

			if (isPaintingWheels) {
				const selectedColor = rimsColors.find((item) => item.name === value);
				dispatch(setRimsColor(selectedColor?.index || 0));
				return;
			}

			const currentPaintType = activePaintGroup?.type;
			const isPearl = currentPaintType === VehiclePaintType.Pearl;

			if (!currentPaintType) {
				return;
			}

			if (isPearl) {
				const selectedColor = pearlColors.find((item) => item.name === value);
				dispatch(setPearlColor(selectedColor?.index || 0));
				return;
			}

			if (!isRgbPaintType(currentPaintType)) {
				const selectedColor = vehicleIndexedColors.find(
					(item) => item.name === value,
				);
				dispatch(setIndexedColor(selectedColor?.index || 0));
			}
		},
		[
			isBrakes,
			brakesColorList,
			activePaintGroup,
			pearlColors,
			vehicleIndexedColors,
			isPaintingWheels,
			rimsColors,
		],
	);

	useEffect(() => {
		if (!activePaintGroup && paintGroups.length > 0 && !isPaintingWheels) {
			if (isPaintingInterior || isBrakes) {
				const rgbGroup = paintGroups.find((group) =>
					isRgbPaintType(group.type),
				);
				if (rgbGroup) {
					dispatch(setGroup(rgbGroup));
				}
			} else if (isPaintingWorks) {
				dispatch(setGroup(paintGroups[0]));
			}
		}
	}, [
		activePaintGroup,
		paintGroups,
		isPaintingWheels,
		isPaintingInterior,
		isPaintingWorks,
		dispatch,
	]);

	const shouldShowPaintGroupSelect =
		!isPaintingWheels &&
		!isPaintingInterior &&
		!isBrakes &&
		paintGroups.length > 0;
	const shouldShowColorSelect = colorList.length > 0;
	const shouldShowRGBPalette =
		isPaintingInterior ||
		(!isBrakes &&
			isPaintingWorks &&
			activePaintGroup &&
			isRgbPaintType(activePaintGroup.type));

	return (
		<StyledContainer>
			<SectionHeading
				text={currentItem.name}
				icon={currentItem.icon}
				textOpacity={1}
				iconOpacity={1}
			/>
			<Divider
				$direction="H"
				$color="#ffffff"
				style={{ marginTop: 20, marginBottom: 20, opacity: 0.1 }}
			/>
			<StyledSettingsContainer>
				{!isPaintingWheels && !isBrakes && (
					<StyledSelectContainer
						$grid={shouldShowPaintGroupSelect && shouldShowColorSelect}
					>
						{shouldShowPaintGroupSelect && (
							<Select
								items={paintGroups.map((item) => item.name)}
								value={activePaintGroup?.name || paintGroups[0]?.name || ''}
								onChange={handleSelectPaintGroup}
								dropdownWidth={
									shouldShowPaintGroupSelect && shouldShowColorSelect
										? '280px'
										: '100%'
								}
								dropdownHeight="320px"
							/>
						)}
						{shouldShowColorSelect && (
							<Select
								items={colorList.map((item) => item.name)}
								value={activeColor?.name || ''}
								onChange={handleSelectColor}
								dropdownWidth={
									shouldShowPaintGroupSelect && shouldShowColorSelect
										? '280px'
										: '100%'
								}
								dropdownHeight="320px"
							/>
						)}
					</StyledSelectContainer>
				)}

				{shouldShowRGBPalette && <RGBPalette />}
				{isPaintingWheels && (
					<StyledPaintingWheelsContainer>
						<StyledPainingWheelsInfoContainer>
							<Text style={{ opacity: 0.5 }}>Цвет дисков</Text>
							<Text style={{ fontWeight: 500 }}>{activeColor?.name ?? ''}</Text>
						</StyledPainingWheelsInfoContainer>
						<StyledPaintingWheelsColorsGridWrapper
							ref={paintingWheelsGridContainerRef}
						>
							<StyledPaintingWheelsColorsGridMaskViewport
								$leftFade={paintingWheelsGridShowLeftFade}
							>
								<StyledPaintingWheelsColorsGridTrack
									ref={paintingWheelsGridTrackRef}
									style={{
										transform: `translateX(${-paintingWheelsGridOffset}px)`,
									}}
								>
									<StyledPaintingWheelsColorsGrid>
										{(rimsColors as RimsColorForUi[]).map((item) => (
											<ColorSelectColorItem
												key={item.index}
												color={rimsColorPreviewCss(item.hex)}
												selected={item.index === rimsColor}
												onClick={() => handlePaintingWheelsGridPick(item.index)}
											/>
										))}
									</StyledPaintingWheelsColorsGrid>
								</StyledPaintingWheelsColorsGridTrack>
							</StyledPaintingWheelsColorsGridMaskViewport>
							<StyledPaintingWheelsColorsGridFadeRight
								$visible={paintingWheelsGridShowRightFade}
							/>
							<StyledGridButton
								$visible={!paintingWheelsGridAtStart}
								style={{ left: 0 }}
								onClick={goPaintingWheelsGridToStart}
							>
								<ImgIcon icon={arrowLeft} width={13} height={13} />
							</StyledGridButton>
							<StyledGridButton
								$visible={!paintingWheelsGridAtEnd}
								style={{ right: 0 }}
								onClick={goPaintingWheelsGridToEnd}
							>
								<ImgIcon icon={arrowRight} width={13} height={13} />
							</StyledGridButton>
						</StyledPaintingWheelsColorsGridWrapper>
					</StyledPaintingWheelsContainer>
				)}
				{isBrakes && (
					<StyledPaintingWheelsContainer>
						<StyledPainingWheelsInfoContainer>
							<Text style={{ opacity: 0.5 }}>Цвет тормозов</Text>
							<Text style={{ fontWeight: 500 }}>{activeColor?.name ?? ''}</Text>
						</StyledPainingWheelsInfoContainer>
						<StyledPaintingWheelsColorsGridWrapper ref={brakesGridContainerRef}>
							<StyledPaintingWheelsColorsGridMaskViewport
								$leftFade={brakesGridShowLeftFade}
							>
								<StyledPaintingWheelsColorsGridTrack
									ref={brakesGridTrackRef}
									style={{
										transform: `translateX(${-brakesGridOffset}px)`,
									}}
								>
									<StyledPaintingWheelsColorsGrid>
										{(brakesColorList as RimsColorForUi[]).map((item) => (
											<ColorSelectColorItem
												key={item.index}
												color={rimsColorPreviewCss(item.hex)}
												selected={item.index === brakesColor}
												onClick={() => handleBrakesGridPick(item.index)}
											/>
										))}
									</StyledPaintingWheelsColorsGrid>
								</StyledPaintingWheelsColorsGridTrack>
							</StyledPaintingWheelsColorsGridMaskViewport>
							<StyledPaintingWheelsColorsGridFadeRight
								$visible={brakesGridShowRightFade}
							/>
							<StyledGridButton
								$visible={!brakesGridAtStart}
								style={{ left: 0 }}
								onClick={goBrakesGridToStart}
							>
								<ImgIcon icon={arrowLeft} width={13} height={13} />
							</StyledGridButton>
							<StyledGridButton
								$visible={!brakesGridAtEnd}
								style={{ right: 0 }}
								onClick={goBrakesGridToEnd}
							>
								<ImgIcon icon={arrowRight} width={13} height={13} />
							</StyledGridButton>
						</StyledPaintingWheelsColorsGridWrapper>
					</StyledPaintingWheelsContainer>
				)}
			</StyledSettingsContainer>
		</StyledContainer>
	);
};

export { ColorSelect };
